import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useMarkAllRead, useMarkRead, useNotifications, useUnreadCount } from '../hooks/useNotifications';

/**
 * Header bell + unread badge + dropdown of the 10 most recent. Click bell to toggle the
 * dropdown; click outside to close. Mark-read mutates per-item; "Mark all read" mutates
 * everything at once. Background poll handled by useUnreadCount (60s + window focus).
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const unread = useUnreadCount();
  const list = useNotifications({ page: 1, pageSize: 10 }, false);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const count = unread.data ?? 0;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={count > 0 ? `${count} unread notifications` : 'Notifications'}
        className="relative w-9 h-9 rounded-md hover:bg-slate-200 inline-flex items-center justify-center text-slate-700"
      >
        <BellIcon />
        {count > 0 ? (
          <span className="absolute -top-1 -right-1 bg-brand-blue text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none min-w-[18px] text-center">
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg border border-slate-200 z-30 max-h-96 overflow-auto"
        >
          <header className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
            <span className="text-sm font-semibold">Notifications</span>
            {count > 0 ? (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                className="text-xs text-brand-blue hover:underline"
              >
                Mark all read
              </button>
            ) : null}
          </header>

          {list.isLoading ? (
            <div className="px-3 py-4 text-sm text-slate-500">Loading…</div>
          ) : list.data && list.data.items.length === 0 ? (
            <div className="px-3 py-6 text-sm text-slate-500 text-center">You're all caught up.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {list.data?.items.map((n) => (
                <li key={n.id} className={`px-3 py-2 text-sm ${n.readAt ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{n.title}</div>
                      <div className="text-slate-600 text-xs">{n.body}</div>
                      <div className="text-slate-400 text-[10px] mt-1">
                        {new Date(n.createdAt).toLocaleString()}
                      </div>
                    </div>
                    {!n.readAt ? (
                      <button
                        type="button"
                        onClick={() => markRead.mutate(n.id)}
                        className="text-xs text-brand-blue hover:underline shrink-0"
                      >
                        Mark read
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <footer className="border-t border-slate-200 px-3 py-2 text-center">
            <NavLink to="/notifications" onClick={() => setOpen(false)} className="text-xs text-brand-blue hover:underline">
              See all
            </NavLink>
          </footer>
        </div>
      ) : null}
    </div>
  );
}

function BellIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
